/**
 * Display Mode Settings Component
 * 
 * Provides controls for switching planet display modes between:
 * - symbols: ☉, ☽, ♂, etc.
 * - short: Sy, Ch, Ma, etc. (two-letter abbreviations from commit e80c574d)
 * - full: Surya, Chandra, Mangala, etc.
 */

import { devWarn } from '../../../utils/devLog';
import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Select,
  Box,
  Divider,
  Switch
} from '@chakra-ui/react';
import { useDisplayModeService, type PlanetDisplayMode } from './services/DisplayModeService';
import { globalEventStream } from './universal_architecture/core/EventStream';
import { FONTS } from '../../../config/fonts';

interface DisplayModeSettingsProps {
  className?: string;
}

/**
 * Get display mode description
 */
const getDisplayModeDescription = (mode: PlanetDisplayMode): string => {
  switch (mode) {
    case 'symbols':
      return 'Unicode symbols (☉, ☽, ♂, ☿, ♃, ♀, ♄, ☊, ☋)';
    case 'short':
      return 'Two-letter abbreviations (Sy, Ch, Ma, Bu, Gu, Sk, Sa, Ra, Ke)';
    case 'full':
      return 'Full Sanskrit names (Surya, Chandra, Mangala, Budha, Guru, Shukra, Shani, Rahu, Ketu)';
    default:
      return 'Unknown mode';
  }
};

/**
 * Display Mode Settings Component
 */
export const DisplayModeSettings: React.FC<DisplayModeSettingsProps> = ({
  className
}) => {
  const { config, setDisplayMode } = useDisplayModeService();

  // Center image options
  const CENTER_IMAGE_OPTIONS = [
    { id: 'aum', label: 'Aum', path: '/img/aum-small.png' },
    { id: 'bhrigu', label: 'Bhrigu', path: '/img/Bhrigu Transparent Background.png' },
  ];

  // Show Images setting (previously Show Logo) - default: true
  const [showImages, setShowImages] = useState(() => {
    if (typeof window !== 'undefined' && typeof window.globalShowLogo !== 'undefined') {
      return window.globalShowLogo;
    }
    try {
      const saved = localStorage.getItem('user-settings-showLogo');
      // Default to true if not set
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });

  // Center image selection - default: 'bhrigu'
  const [centerImage, setCenterImage] = useState(() => {
    if (typeof window !== 'undefined' && window.globalCenterImage) {
      return window.globalCenterImage;
    }
    try {
      const saved = localStorage.getItem('user-settings-centerImage');
      return saved || 'bhrigu';
    } catch {
      return 'bhrigu';
    }
  });

  // Rashi display mode - 'symbols', 'full', or 'devanagari'
  const [rashiDisplayMode, setRashiDisplayMode] = useState<'symbols' | 'full' | 'devanagari'>(() => {
    if (typeof window !== 'undefined' && (window as any).globalRashiDisplayMode) {
      return (window as any).globalRashiDisplayMode;
    }
    try {
      const saved = localStorage.getItem('user-settings-rashiDisplayMode');
      return (saved === 'symbols' || saved === 'full' || saved === 'devanagari') ? saved : 'symbols';
    } catch {
      return 'symbols';
    }
  });

  // Show Rasi Names setting - global for all charts, default: true
  const [showRasiNames, setShowRasiNames] = useState(() => {
    try {
      const saved = localStorage.getItem('hora-showRasiNames');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });

  // Sync showImages with global flag and emit event
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.globalShowLogo = showImages;
      try {
        localStorage.setItem('user-settings-showLogo', String(showImages));
      } catch (error) {
        devWarn('Failed to save showImages to localStorage:', error);
      }
    }

    // Emit event for chart re-render
    globalEventStream.emit({
      id: `logo-visibility-changed-${Date.now()}`,
      type: 'settings.logo.changed',
      source: 'display-mode-settings',
      timestamp: Date.now(),
      showLogo: showImages
    });
  }, [showImages]);

  // Sync centerImage with global flag and emit event
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.globalCenterImage = centerImage;
      try {
        localStorage.setItem('user-settings-centerImage', centerImage);
      } catch (error) {
        devWarn('Failed to save centerImage to localStorage:', error);
      }
    }

    // Emit event for chart re-render
    globalEventStream.emit({
      id: `center-image-changed-${Date.now()}`,
      type: 'settings.centerImage.changed',
      source: 'display-mode-settings',
      timestamp: Date.now(),
      centerImage
    });
  }, [centerImage]);

  // Sync rashiDisplayMode with global flag and emit event
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).globalRashiDisplayMode = rashiDisplayMode;
      try {
        localStorage.setItem('user-settings-rashiDisplayMode', rashiDisplayMode);
      } catch (error) {
        devWarn('Failed to save rashiDisplayMode to localStorage:', error);
      }
    }

    // Emit event for chart re-render
    globalEventStream.emit({
      id: `rashi-display-mode-${Date.now()}`,
      type: 'settings.rashiDisplayMode.changed',
      source: 'display-mode-settings',
      timestamp: Date.now(),
      rashiDisplayMode
    });
  }, [rashiDisplayMode]);

  // Sync showRasiNames with localStorage and emit event
  useEffect(() => {
    try {
      localStorage.setItem('hora-showRasiNames', String(showRasiNames));
    } catch (error) {
      devWarn('Failed to save showRasiNames to localStorage:', error);
    }

    // Emit event for chart re-render
    globalEventStream.emit({
      id: `rasi-names-visibility-${Date.now()}`,
      type: 'settings.rasiNames.changed',
      source: 'display-mode-settings',
      timestamp: Date.now(),
      showRasiNames
    });
  }, [showRasiNames]);

  const handleModeChange = (newMode: PlanetDisplayMode) => {
    setDisplayMode(newMode);

    // Emit event for all charts to update planet display
    globalEventStream.emit({
      id: `planet-display-mode-${Date.now()}`,
      type: 'planet.displayMode.change',
      source: 'display-mode-settings',
      timestamp: Date.now(),
      data: { mode: newMode }
    });
  };

  return (
    <Box className={className}>
      <VStack spacing={4} align="stretch">

        {/* PLANETS SECTION */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
            Planets
          </Text>
          <HStack justify="space-between" align="center">
            <Text fontSize="sm" color="gray.600">
              Display Format:
            </Text>
            <HStack spacing={2}>
              <Select
                value={config.mode}
                onChange={(e) => handleModeChange(e.target.value as PlanetDisplayMode)}
                size="sm"
                width="124px"
                variant="filled"
              >
                <option value="symbols">Symbols</option>
                <option value="short">Short</option>
                <option value="full">Full</option>
              </Select>
              <Text
                fontSize="sm"
                color="gray.500"
                fontFamily={config.mode === 'symbols' ? FONTS.chart.symbols : 'inherit'}
                minW="94px"
              >
                {config.mode === 'symbols' ? 'Q, W, E' : config.mode === 'short' ? 'Sy, Ch, Ma' : 'Surya...'}
              </Text>
            </HStack>
          </HStack>
        </Box>

        <Divider />

        {/* RASI SECTION */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
            Rasi
          </Text>
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                Display Format:
              </Text>
              <HStack spacing={2}>
                <Select
                  value={rashiDisplayMode}
                  onChange={(e) => setRashiDisplayMode(e.target.value as 'symbols' | 'full' | 'devanagari')}
                  size="sm"
                  width="124px"
                  variant="filled"
                >
                  <option value="symbols">Symbols</option>
                  <option value="full">Full</option>
                  <option value="devanagari">Devanagari</option>
                </Select>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontFamily={rashiDisplayMode === 'symbols' ? FONTS.chart.symbols : 'inherit'}
                  minW="94px"
                >
                  {rashiDisplayMode === 'symbols' ? 'a, s, d' : rashiDisplayMode === 'devanagari' ? 'मेष, वृषभ' : 'Mesha...'}
                </Text>
              </HStack>
            </HStack>
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                Show Names:
              </Text>
              <Switch
                size="sm"
                isChecked={showRasiNames}
                onChange={(e) => setShowRasiNames(e.target.checked)}
                colorScheme="green"
              />
            </HStack>
          </VStack>
        </Box>

        <Divider />

        {/* IMAGES SECTION */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
            Images
          </Text>
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                Show Images:
              </Text>
              <Switch
                size="sm"
                isChecked={showImages}
                onChange={(e) => setShowImages(e.target.checked)}
                colorScheme="green"
              />
            </HStack>

            {/* Center Image Selector - only show when images are enabled */}
            {showImages && (
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">
                  Center Image:
                </Text>
                <Select
                  value={centerImage}
                  onChange={(e) => setCenterImage(e.target.value)}
                  size="sm"
                  width="120px"
                  variant="filled"
                >
                  {CENTER_IMAGE_OPTIONS.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </HStack>
            )}
          </VStack>
        </Box>

      </VStack>
    </Box>
  );
};


export default DisplayModeSettings;